/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { TextBox as DXTextBox } from 'devextreme-react/text-box';
import { Validator, RequiredRule, CustomRule, StringLengthRule } from 'devextreme-react/validator';
import '../../../scss/controls/_textbox.scss';
import { FocusInEvent, KeyUpEvent, ValueChangedEvent } from 'devextreme/ui/text_box';
import { ValidatedEvent } from 'devextreme/ui/validator';
import { InfoIconButton } from '../InfoIconButton';

export enum TextBoxComponentSpacing {
    None = 'noSpacing',
    SmallSpacing = 'smallSpacing',
    StandardSpacing = 'standardSpacing',
    LargeSpacing = 'largeSpacing',
}

/** Where the label sits relative to the input. */
export enum TextBoxLayoutStyle {
    /** Label above the input (default; the original layout). */
    Vertical = 'vertical',
    /** Label to the left of the input, in a fixed-width column (see labelWidth). */
    Horizontal = 'horizontal',
}

interface TextBoxProps {
    id?: string;
    dataTestid?: string;
    value: string | undefined;
    name?: string;
    type?: 'email' | 'password' | 'search' | 'tel' | 'text' | 'url';
    readOnly?: boolean;
    disabled?: boolean;
    label?: string;
    helperText?: React.ReactNode;
    required?: boolean;
    children?: React.ReactNode;
    validator?: React.ReactNode;
    width?: string;
    maxLength?: number;
    mask?: string;
    maskChar?: string;
    maskInvalidMessage?: string;
    onChange?: (value: string) => void;
    onChangeWithMask?: (value?: string) => void;
    onEnterKey?: () => void;
    onKeyUp?: ((e: KeyUpEvent) => void) | undefined;
    onFocusIn?: ((e: FocusInEvent) => void) | undefined;
    validateValue?: (value: string) => boolean;
    onValidationEvent?: (isValid: boolean) => void;
    validateValueMessage?: string;
    placeholder?: string;
    componentSpacing?: TextBoxComponentSpacing;
    showInfoIcon?: boolean;
    infoText?: React.ReactNode;
    /** Label above the input (Vertical, default) or beside it (Horizontal). */
    layoutStyle?: TextBoxLayoutStyle;
    /** Width of the label column when layoutStyle is Horizontal. Defaults to 120px. Ignored for Vertical. */
    labelWidth?: string;
    /** Total height of the input box, border included (e.g. '36px'). Omit to keep the default height. */
    height?: string;
}

// Allowing "any" because it is defined by DevExtreme
export interface ValidationCallbackOptions {
    column: Record<string, any>;
    data: Record<string, any>;
    formItem: Record<string, any>;
    rule: Record<string, any>;
    validator: Record<string, any>;
    value: string | number;
}

export const TextBox: React.FC<TextBoxProps> = ({
    id,
    dataTestid,
    value,
    mask,
    maskChar,
    maskInvalidMessage,
    type = 'text',
    readOnly = false,
    disabled = false,
    label,
    helperText,
    required = false,
    children,
    validator,
    width = 'auto',
    maxLength,
    onChange,
    onChangeWithMask,
    onEnterKey,
    onFocusIn,
    validateValue,
    onValidationEvent,
    validateValueMessage,
    placeholder,
    componentSpacing = TextBoxComponentSpacing.StandardSpacing,
    showInfoIcon = false,
    infoText,
    layoutStyle = TextBoxLayoutStyle.Vertical,
    labelWidth,
    height,
}) => {
    const [valueChangeEvent, set_valueChangeEvent] = useState('change');

    // With the defaults (Vertical, no height) this is the exact class name and inline style the control has
    // always rendered, so existing usages are unaffected. Each opt-in only adds a class + a CSS variable.
    const isHorizontal = layoutStyle === TextBoxLayoutStyle.Horizontal;
    const hasFixedHeight = !!height;
    const containerClassName = `textbox-container ${componentSpacing}${isHorizontal ? ' horizontal' : ''}${hasFixedHeight ? ' fixedHeight' : ''}`;
    const containerStyle: React.CSSProperties = {
        width: width,
        ...(isHorizontal ? { '--textbox-label-width': labelWidth ?? '120px' } : {}),
        ...(hasFixedHeight ? { '--textbox-height': height } : {}),
    } as React.CSSProperties;
    const [errorMessage, set_errorMessage] = useState('');
    const [fieldLengthError, set_fieldLengthError] = useState(false);

    const handleValueChanged = (e: ValueChangedEvent) => {
        // The native maxlength attribute stops typing and pasting, but browser autofill, drag-and-drop and
        // programmatic value sets bypass it. Clamp here so an over-length value never reaches the store or the API.
        if (maxLength && typeof e.value === 'string' && e.value.length > maxLength) {
            set_fieldLengthError(true);
            e.component.option('value', e.value.slice(0, maxLength));
            return;
        }

        if (onChange) onChange(e.value);
        if (onChangeWithMask) onChangeWithMask(e.component.option('text'));
        set_valueChangeEvent('input');
    };

    const onValidated = (validatedInfo: ValidatedEvent) => {
        set_errorMessage(validatedInfo?.brokenRule?.message ?? '');
        if (onValidationEvent) {
            const isValid = validatedInfo.isValid ?? false;
            onValidationEvent?.(isValid);
        }
    };

    const validationCallback = (options: ValidationCallbackOptions): boolean => {
        if (!validateValue) return true;

        const result = validateValue(options.value?.toString()) ?? false;

        if (onValidationEvent) onValidationEvent(result);

        return result;
    };

    const onKeyDown = (e: any) => {
        const currentValue = e.event.currentTarget.value;
        const key = e.event.key;

        // Allow control keys (Backspace, Tab, Arrow keys, etc.)
        const controlKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
        if (controlKeys.includes(key)) {
            set_fieldLengthError(false);
            return;
        }

        // Detect attempted 5th digit
        if (maxLength && currentValue.length >= maxLength) {
            set_fieldLengthError(true);
            // Prevent the key from being entered
            e.event.preventDefault();
            return;
        }

        set_fieldLengthError(false);
    };

    const onKeyUp = (e: any) => {
        const currentValue = e.event.currentTarget.value;
        // Existing validation logic
        if (validateValue && onValidationEvent) {
            const isValid = validateValue(currentValue);
            onValidationEvent(isValid);
        }
    };

    return (
        <div
            className={containerClassName}
            style={containerStyle}
        >
            {label && (
                <div>
                    <label className={`textbox-label ${errorMessage ? 'error' : ''}`}>{required ? label + ' *' : label}</label>
                    {showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            <div
                className='textbox-wrapper'
                style={{ position: 'relative' }}
            >
                <DXTextBox
                    id={id}
                    data-testid={dataTestid}
                    mode={type}
                    mask={mask}
                    maskChar={maskChar}
                    maskInvalidMessage={maskInvalidMessage}
                    name={type === 'password' ? 'current-password' : type}
                    inputAttr={{
                        autoComplete: type === 'password' ? 'current-password' : type,
                    }}
                    value={value}
                    valueChangeEvent={valueChangeEvent}
                    disabled={disabled}
                    onValueChanged={handleValueChanged}
                    onKeyDown={onKeyDown}
                    onKeyUp={onKeyUp}
                    onFocusIn={onFocusIn}
                    readOnly={readOnly}
                    className={`textbox-input ${errorMessage ? 'error' : ''}`}
                    width={width}
                    onEnterKey={onEnterKey}
                    placeholder={placeholder}
                    maxLength={maxLength}
                >
                    <Validator onValidated={onValidated}>
                        {required && <RequiredRule message='This field is required' />}
                        {!!maxLength && (
                            <StringLengthRule
                                max={maxLength}
                                message={`This field will not allow more than ${maxLength} characters.`}
                            />
                        )}
                        {validator}
                        {validateValue && validationCallback && (
                            <CustomRule
                                validationCallback={validationCallback}
                                message={validateValueMessage}
                            />
                        )}
                    </Validator>
                </DXTextBox>
            </div>
            {/* Show helper text only if there is no error */}
            {errorMessage ? (
                <div className='textbox-error'>{errorMessage}</div>
            ) : maxLength && fieldLengthError ? (
                <div className='textbox-error'>This field will not allow more than {maxLength} characters. </div>
            ) : (
                helperText && <div className='textbox-helper'>{helperText}</div>
            )}
            {children && <div className='textbox-children'>{children}</div>}
        </div>
    );
};
